import React, { useEffect, useState, useCallback } from "react";
import { Box, Fab, Fade } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";

const FloatingSurveyBubble = ({ targetId = "survey" }) => {
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Observe DOM for any open MUI dialog/modal (role="dialog" or role="presentation")
  useEffect(() => {
    const checkForDialogs = () => {
      const hasDialog = document.querySelector(
        '[role="dialog"], [role="presentation"], .MuiDialog-root, .MuiModal-root'
      );
      setDialogOpen(!!hasDialog);
    };

    // Use MutationObserver to detect dialog open/close
    const observer = new MutationObserver(checkForDialogs);
    observer.observe(document.body, { childList: true, subtree: true });

    // Initial check
    checkForDialogs();

    return () => observer.disconnect();
  }, []);

  const updateVisibility = useCallback(() => {
    const target = document.getElementById(targetId);
    if (!target) {
      setVisible(false);
      return;
    }
    const rect = target.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    // Hide only while any part of the survey section is on-screen.
    const inView = rect.top < viewportHeight && rect.bottom > 0;
    setVisible(!inView);
  }, [targetId]);

  useEffect(() => {
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);

    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, [updateVisibility]);

  const handleClick = () => {
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Fade in={visible && !dialogOpen} unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          bottom: 96,
          right: 32,
          zIndex: 1999,
        }}
      >
        <Fab
          variant="extended"
          color="primary"
          aria-label="Take the 1 minute survey"
          onClick={handleClick}
          sx={{
            bgcolor: "#0B3D6E",
            color: "#FFF",
            height: 56,
            px: 3,
            fontWeight: "bold",
            textTransform: "none",
            fontFamily: "inherit",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            "&:hover": {
              bgcolor: "#072844",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <AssignmentIcon sx={{ mr: 1, fontSize: "1.5rem" }} />
          1min Survey
        </Fab>
      </Box>
    </Fade>
  );
};

export default FloatingSurveyBubble;
